import { harborUrl, satelliteUrl } from './urls';

export const HQ_NAME = 'Co-parenting HQ';
export const HQ_TAGLINE =
  'The starting place for coparents, whether you are about to share kids or already are.';
export const HQ_DESCRIPTION =
  'Free tools and practical guides for soon-to-be and existing coparents: first-week checklists, schedules, expenses, messages, visitation logs, and what to do if a court named another app. Not legal advice.';
export const HQ_HERO =
  'Know the next useful thing to do, whether you just separated or have been sharing kids for years.';

export type HqLinkKind = 'hq' | 'satellite' | 'harbor';

export type HqLink = {
  label: string;
  href: string;
  kind: HqLinkKind;
};

export type HqStep = {
  label: string;
  href: string;
  kind: HqLinkKind;
};

export type HqStage = {
  id: string;
  title: string;
  blurb: string;
  steps: HqStep[];
};

export type HqPain = {
  id: string;
  title: string;
  blurb: string;
  href: string;
  kind: HqLinkKind;
};

export type HqToolCategory = 'schedule' | 'money' | 'records' | 'plans' | 'check-in';

export type HqTool = {
  id: string;
  name: string;
  blurb: string;
  href: string;
  kind: HqLinkKind;
  category: HqToolCategory;
  categoryLabel: string;
  output: string;
  homePreview?: boolean;
};

export const STAGES: HqStage[] = [
  {
    id: 'still-deciding',
    title: 'Still deciding / still in the house',
    blurb: 'You do not need a full plan this week. You need the kids stable and a short record.',
    steps: [
      { label: 'First-week checklist', href: '/guides/first-week', kind: 'hq' },
      { label: 'When a lawyer is actually needed', href: '/guides/need-a-lawyer', kind: 'hq' },
      { label: 'Start a facts-only record', href: '/guides/document-now', kind: 'hq' },
    ],
  },
  {
    id: 'just-separated',
    title: 'Just separated, not filing yet',
    blurb: 'Get a calendar, split the next kid cost, and write down what actually happened.',
    steps: [
      { label: 'How to document before anyone asks', href: '/guides/document-now', kind: 'hq' },
      { label: 'Build a parenting-time calendar', href: satelliteUrl('custodyscheduleapp.com'), kind: 'satellite' },
      { label: 'Split one expense', href: satelliteUrl('coparentmoney.com'), kind: 'satellite' },
    ],
  },
  {
    id: 'filing',
    title: 'Filing or about to file',
    blurb: 'A free state checklist first. Counsel if the kids, money, or safety are contested.',
    steps: [
      { label: 'When self-help is not enough', href: '/guides/need-a-lawyer', kind: 'hq' },
      { label: 'What belongs in a parenting plan', href: '/guides/parenting-plan', kind: 'hq' },
      { label: 'Free per-state divorce checklist', href: harborUrl('/divorce'), kind: 'harbor' },
    ],
  },
  {
    id: 'already-coparenting',
    title: 'Already coparenting',
    blurb: 'Schedule, money, a visitation log, and a plan you can print. No account required.',
    steps: [
      { label: 'Browse the free tools', href: '/tools', kind: 'hq' },
      { label: 'Propose a schedule without a fight', href: '/guides/propose-a-schedule', kind: 'hq' },
      { label: 'How to split kid expenses', href: '/guides/split-expenses', kind: 'hq' },
    ],
  },
  {
    id: 'named-app',
    title: 'A court order named another app',
    blurb: 'Use the named app. Here is what you can still keep as your own file.',
    steps: [
      { label: 'What the order still lets you do solo', href: '/guides/named-app', kind: 'hq' },
      { label: 'Keep a private facts-only record', href: '/guides/document-now', kind: 'hq' },
    ],
  },
];

export const PAINS: HqPain[] = [
  {
    id: 'money',
    title: 'Money fights',
    blurb: 'Split an expense and estimate guideline support.',
    href: satelliteUrl('coparentmoney.com'),
    kind: 'satellite',
  },
  {
    id: 'schedule',
    title: 'The schedule is a mess',
    blurb: 'Build a 2-2-3, week-on/week-off, or weekend rotation.',
    href: satelliteUrl('custodyscheduleapp.com'),
    kind: 'satellite',
  },
  {
    id: 'texts',
    title: 'Hostile texts',
    blurb: 'Write facts, not arguments. Keep a record you can stand behind.',
    href: '/guides/write-a-message',
    kind: 'hq',
  },
  {
    id: 'records',
    title: 'No paper trail',
    blurb: 'A printable log and a court-documentation checklist.',
    href: '/guides/document-now',
    kind: 'hq',
  },
  {
    id: 'no-show',
    title: 'Missed pickups',
    blurb: 'What to write down when the other parent is late or does not show.',
    href: '/guides/missed-exchange',
    kind: 'hq',
  },
  {
    id: 'paperwork',
    title: 'Paperwork / divorce forms',
    blurb: 'See if a self-help packet may fit, before you share names.',
    href: harborUrl('/divorce'),
    kind: 'harbor',
  },
];

export const TOOLS: HqTool[] = [
  {
    id: 'week-on-week-off',
    name: 'Week-on / week-off calendar',
    blurb: 'Pick a start date and exchange day, preview three months, download an .ics file.',
    href: satelliteUrl('weekonweekoff.com'),
    kind: 'satellite',
    category: 'schedule',
    categoryLabel: 'Schedule',
    output: '.ics calendar',
    homePreview: true,
  },
  {
    id: 'custody-schedule',
    name: 'Custody rotation builder',
    blurb: '2-2-3, 2-2-5-5, 3-4-4-3, alternating weeks, or every-weekend, then export.',
    href: satelliteUrl('custodyscheduleapp.com'),
    kind: 'satellite',
    category: 'schedule',
    categoryLabel: 'Schedule',
    output: '.ics calendar',
    homePreview: true,
  },
  {
    id: 'expense-split',
    name: 'Expense-split calculator',
    blurb: '50/50, income pro-rata, or custom percent, plus a reimbursement letter.',
    href: satelliteUrl('coparentmoney.com'),
    kind: 'satellite',
    category: 'money',
    categoryLabel: 'Money',
    output: 'letter you can copy',
    homePreview: true,
  },
  {
    id: 'child-support',
    name: 'Child support estimate',
    blurb: 'A rough guideline estimate for all 50 states and DC. Educational only.',
    href: harborUrl('/child-support-calculator'),
    kind: 'harbor',
    category: 'money',
    categoryLabel: 'Money',
    output: 'state estimate',
    homePreview: true,
  },
  {
    id: 'visitation-log',
    name: 'Visitation log',
    blurb: 'Print an exchange log or a missed-handoff note. Facts only.',
    href: satelliteUrl('visitationtracker.com'),
    kind: 'satellite',
    category: 'records',
    categoryLabel: 'Records',
    output: 'printable log',
    homePreview: true,
  },
  {
    id: 'court-checklist',
    name: 'Court documentation checklist',
    blurb: 'A starting list for modification, support, or relocation. Not legal advice.',
    href: satelliteUrl('courtreadyrecords.com'),
    kind: 'satellite',
    category: 'records',
    categoryLabel: 'Records',
    output: 'printable checklist',
    homePreview: true,
  },
  {
    id: 'parenting-plan',
    name: 'Parenting plan PDF',
    blurb: 'Walk through custody, holidays, communication, and expenses. Email only at download.',
    href: harborUrl('/parenting-plan/builder'),
    kind: 'harbor',
    category: 'plans',
    categoryLabel: 'Plans',
    output: 'PDF draft',
    homePreview: true,
  },
  {
    id: 'divorce-checklist',
    name: 'Uncontested divorce checklist',
    blurb: 'Free per-state steps. A paid packet is optional if it may fit.',
    href: harborUrl('/divorce'),
    kind: 'harbor',
    category: 'plans',
    categoryLabel: 'Plans',
    output: 'state checklist',
    homePreview: true,
  },
  {
    id: 'rate-coparent',
    name: 'Rate my coparent',
    blurb: 'Two at a time. Vote on situations, then unlock a shareable card. No names.',
    href: satelliteUrl('ratemycoparent.com'),
    kind: 'satellite',
    category: 'check-in',
    categoryLabel: 'Check-in',
    output: 'shareable card',
  },
  {
    id: 'rate-coparenting',
    name: 'Rate my coparenting',
    blurb: 'Two at a time. Vote on how a parent handled it, then unlock a shareable card. No names.',
    href: satelliteUrl('ratemycoparenting.com'),
    kind: 'satellite',
    category: 'check-in',
    categoryLabel: 'Check-in',
    output: 'shareable card',
  },
];

export const TOOL_GROUPS: { id: HqToolCategory; label: string; blurb: string }[] = [
  { id: 'schedule', label: 'Schedule', blurb: 'Build a rotation and take it home as a calendar file.' },
  { id: 'money', label: 'Money', blurb: 'Split one cost or estimate guideline support.' },
  { id: 'records', label: 'Records', blurb: 'A log you can print. Facts only.' },
  { id: 'plans', label: 'Plans', blurb: 'A draft you can walk through before anyone files.' },
  { id: 'check-in', label: 'Check-in', blurb: 'Two-at-a-time votes. No names, not a court exhibit.' },
];

export const PROFESSIONALS_LINK: HqLink = {
  label: 'For lawyers and coaches',
  href: harborUrl('/learn/practitioners'),
  kind: 'harbor',
};
